import axios, { AxiosError, type AxiosInstance } from 'axios';
import { credSet, credGet, credClear } from './desktop';

const DEFAULT_SERVER = import.meta.env.VITE_API_URL || '';
const REFRESH_KEY = 'refresh';

/** Read user-chosen server URL (e.g. https://ccark.arkenom.com.tr) or null. */
export function getServerUrl(): string {
  if (typeof window === 'undefined') return DEFAULT_SERVER;
  return localStorage.getItem('ccark.serverUrl') || DEFAULT_SERVER;
}

export function setServerUrl(url: string | null) {
  if (typeof window === 'undefined') return;
  if (url) {
    localStorage.setItem('ccark.serverUrl', url.replace(/\/+$/, ''));
  } else {
    localStorage.removeItem('ccark.serverUrl');
  }
}

/** Compute API base from current server URL. Empty server → relative '/api'. */
function apiBase(): string {
  const server = getServerUrl();
  return server ? `${server}/api` : '/api';
}

/** WebSocket URL for session enforcement + live events (backend /ws gateway). */
export function wsBase(): string {
  const server = getServerUrl();
  if (server) {
    // https:// → wss://, http:// → ws://
    return server.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:') + '/ws';
  }
  if (typeof window === 'undefined') return '/ws';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

let _accessToken: string | null = null;
let _refreshToken: string | null = null;
let _onUnauthorized: (() => void) | null = null;

/**
 * Set tokens. Refresh token is persisted to the OS credential store on
 * Tauri (Windows Credential Manager / macOS Keychain / Linux Secret Service);
 * in a browser it falls back to localStorage with a "ccark.cred." prefix.
 */
export async function setAuthTokens(access: string | null, refresh: string | null): Promise<void> {
  _accessToken = access;
  _refreshToken = refresh;
  if (refresh) {
    await credSet(REFRESH_KEY, refresh);
  } else {
    await credClear(REFRESH_KEY);
  }
  // Migrate legacy plaintext localStorage token if present.
  try { localStorage.removeItem('ccark.refresh'); } catch { /* */ }
}

export async function getRefreshToken(): Promise<string | null> {
  if (_refreshToken) return _refreshToken;
  const stored = await credGet(REFRESH_KEY);
  if (stored) {
    _refreshToken = stored;
    return stored;
  }
  // One-shot migration: pre-keyring builds wrote ccark.refresh in localStorage.
  try {
    const legacy = localStorage.getItem('ccark.refresh');
    if (legacy) {
      await credSet(REFRESH_KEY, legacy);
      localStorage.removeItem('ccark.refresh');
      _refreshToken = legacy;
      return legacy;
    }
  } catch { /* */ }
  return null;
}

export function onUnauthorized(cb: () => void) {
  _onUnauthorized = cb;
}

export const api: AxiosInstance = axios.create({
  baseURL: apiBase(),
  timeout: 15_000,
});

api.interceptors.request.use((config) => {
  // Re-resolve base URL each request so server URL changes take effect live.
  config.baseURL = apiBase();
  if (_accessToken) {
    config.headers.set('Authorization', `Bearer ${_accessToken}`);
  }
  // Inject admin panel 2FA password on /admin/* requests if user has unlocked.
  const url = config.url ?? '';
  if (url.startsWith('/admin') || url.includes('/admin/')) {
    const adminPass = sessionStorage.getItem('ccark.adminPass');
    if (adminPass) config.headers.set('X-Admin-Pass', adminPass);
  }
  return config;
});

export function setAdminPass(pass: string | null) {
  if (pass) sessionStorage.setItem('ccark.adminPass', pass);
  else sessionStorage.removeItem('ccark.adminPass');
}

export function hasAdminPass(): boolean {
  return !!sessionStorage.getItem('ccark.adminPass');
}

let refreshing: Promise<void> | null = null;

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original: any = err.config;
    if (err.response?.status === 401 && !original?._retry) {
      original._retry = true;
      try {
        refreshing ||= refreshAccess();
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
        _onUnauthorized?.();
        throw e;
      }
    }
    throw err;
  },
);

async function refreshAccess(): Promise<void> {
  const rt = await getRefreshToken();
  if (!rt) throw new Error('no_refresh_token');
  const res = await axios.post(`${apiBase()}/auth/refresh`, { refresh_token: rt });
  await setAuthTokens(res.data.access_token, res.data.refresh_token);
}

/** Public helper for callers that want to refresh before the first request. */
export async function refreshTokensNow(): Promise<string | null> {
  try {
    await refreshAccess();
    return _accessToken;
  } catch {
    await setAuthTokens(null, null);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Typed endpoints
// ─────────────────────────────────────────────────────────────

export interface User {
  id: number;
  username: string;
  display_name: string;
  extension: string;
  role: 'admin' | 'leader' | 'agent';
  auto_answer?: boolean;
}

export interface PbxConfig {
  janus_ws_url: string;
  pbx_host: string;
  pbx_port: number;
  pbx_fqdn: string;
  sip_transport: 'udp' | 'tcp' | 'tls';
  ice_servers: RTCIceServer[];
  /** 'relay' forces SRTP through TURN — required for VPN/NAT'd agents. */
  ice_transport_policy: RTCIceTransportPolicy;
  queue_codes: { login: string; logout: string; pause: string; resume: string };
}

export interface SipCredentials {
  username: string;
  auth_id: string;
  secret: string;
}

export interface SpeedDial {
  id: number;
  label: string;
  number: string;
  position: number;
  scope: 'user' | 'global';
}

export interface CrmTemplate {
  id: number;
  name: string;
  url_template: string;
  open_mode: 'new_tab' | 'popup' | 'named' | 'embedded';
  desktop_browser: 'chrome' | 'firefox' | 'edge' | 'default';
  trigger_on: 'ring' | 'answered' | 'ended';
  is_default: boolean;
  is_active: boolean;
  scope: 'user' | 'global';
}

export interface CrmTemplateInput {
  name: string;
  url_template: string;
  open_mode: CrmTemplate['open_mode'];
  desktop_browser?: CrmTemplate['desktop_browser'];
  trigger_on?: CrmTemplate['trigger_on'];
  is_default?: boolean;
  is_active?: boolean;
}

export const Auth = {
  login: (username: string, password: string) =>
    api.post<{ access_token: string; refresh_token: string; user: User }>('/auth/login', {
      username,
      password,
    }).then((r) => r.data),

  logout: () => api.post('/auth/logout'),

  me: () => api.get<{ user: User | null }>('/auth/me').then((r) => r.data),
};

export const Config = {
  pbx: () => api.get<PbxConfig>('/config').then((r) => r.data),
  sipCreds: () => api.get<SipCredentials>('/sip-credentials').then((r) => r.data),
};

export interface SipHealth {
  extension: string;
  /** 3CX'in agent'i kayıtlı görmesi. False = bizim için "Hazır" yalandır. */
  registered: boolean;
  enabled: boolean;
  /** 3CX kuyruk login durumu — 'LoggedIn' | 'LoggedOut' | null. Frontend queueIn'i
   *  buradan sync eder (logout/refresh sonrası gerçek durumu yansıtsın). */
  queue_status?: string | null;
  /** False ise 3CX xapi'sine ulaşılamadı — `registered` field güvenilmez. */
  pbx_reachable: boolean;
  error?: string;
  ts: number;
}

export const Health = {
  sipRegistration: () => api.get<SipHealth>('/health/sip-registration').then((r) => r.data),
};

export const SpeedDials = {
  list: () => api.get<SpeedDial[]>('/speed-dials').then((r) => r.data),
  create: (input: { label: string; number: string; position?: number }) =>
    api.post<SpeedDial>('/speed-dials', input).then((r) => r.data),
  update: (id: number, input: Partial<{ label: string; number: string; position: number }>) =>
    api.patch<SpeedDial>(`/speed-dials/${id}`, input).then((r) => r.data),
  remove: (id: number) => api.delete(`/speed-dials/${id}`),
};

export const CrmTemplates = {
  list: () => api.get<CrmTemplate[]>('/crm-templates').then((r) => r.data),
  create: (input: Omit<CrmTemplate, 'id' | 'scope' | 'is_default'> & { is_default?: boolean }) =>
    api.post<CrmTemplate>('/crm-templates', input).then((r) => r.data),
  update: (id: number, input: Partial<Omit<CrmTemplate, 'id' | 'scope'>>) =>
    api.patch<CrmTemplate>(`/crm-templates/${id}`, input).then((r) => r.data),
  remove: (id: number) => api.delete(`/crm-templates/${id}`),
  setDefault: (id: number) => api.post(`/crm-templates/${id}/default`),
};

// ─────────────────────────────────────────────────────────────
// Admin endpoints (require role=admin)
// ─────────────────────────────────────────────────────────────

export interface AdminUser {
  id: number;
  username: string;
  display_name: string;
  extension: string;
  role: 'admin' | 'leader' | 'agent';
  is_active: boolean;
  auto_answer: boolean;
  created_at: string;
  updated_at: string;
}

export interface PbxExtension {
  id: number;
  number: string;
  display_name: string;
  email: string | null;
  enabled: boolean;
  is_registered: boolean;
  queue_status: string | null;
  primary_group_id: number | null;
}

export interface ActivityLogEntry {
  id: number;
  userId: number | null;
  type: string;
  refId: string | null;
  meta: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
}

/**
 * Agent kuyruk / mola eylemleri. Backend 3CX xapi'sine PATCH atar
 * (feature code dial yerine), eski 3CX UI'da kanıtlanmış pattern.
 *
 * 3CX V20 profile sabitleri:
 *   - Available     — kuyruğa açık, çağrı kabul ediyor
 *   - Available     — hazır (kuyruktan inbound alır)
 *   - Away          — kısa mola
 *   - Out of office — yemek molası (eski IGDAS projesi mapping'i)
 *   - Custom 1      — dış arama (outbound duty)
 *   - Custom 2      — admin-assigned (Eğitim/Toplantı/Sınav/Soru Molası/Geri Bildirim)
 *                     ProfileMessage ile alt-tip ayrımı yapılır
 *
 * Profile name'ler boşluklu — 3CX V20 internal storage formu.
 * "Lunch"/"BusinessTrip" 3CX native değil (Teams entegrasyonu için).
 */
export type AgentProfile = 'Available' | 'Away' | 'Out of office' | 'Custom 1' | 'Custom 2';

export const Agent = {
  /** Set agent's 3CX profile + optional ProfileMessage (Custom1/2 alt-tipi için). */
  setProfile: (profile: AgentProfile, customMessage?: string) =>
    api
      .post<{ ok: true; profile: AgentProfile; customMessage: string }>('/agent/profile', {
        profile,
        ...(customMessage ? { customMessage } : {}),
      })
      .then((r) => r.data),

  /** Kuyruğa giriş/çıkış. Profile'dan ayrı — 3CX QueueStatus field'ı. */
  setQueue: (loggedIn: boolean) =>
    api.post<{ ok: true; loggedIn: boolean }>('/agent/queue', { loggedIn }).then((r) => r.data),

  /** Kümülatif login + kuyruk süreleri (vardiya penceresi içinde). */
  durations: () =>
    api.get<AgentDurations>('/agent/durations').then((r) => r.data),
};

export interface AgentDurations {
  shiftLoginAt: string | null;
  onlineAccumulatedMs: number;
  onlineOpenSince: string | null;
  queueAccumulatedMs: number;
  queueOpenSince: string | null;
  breakAccumulatedMs: number;
  breakOpenSince: string | null;
  lastCallEndedAt: string | null;
  shiftHours: number;
  now: string;
}

export const Settings = {
  get: () => api.get<{ shiftHours: number }>('/settings').then((r) => r.data),
  setShiftHours: (shiftHours: number) =>
    api.put<{ ok: true; shiftHours: number }>('/settings/shift-hours', { shiftHours }).then((r) => r.data),
};

// ─── Takım Lideri (leader) ──────────────────────────────────────────────────

export type AgentLiveStatus =
  | 'cevrimdisi' | 'kuyruk-disi' | 'hazir' | 'mola'
  | 'yemek' | 'dis-arama' | 'ozel' | 'bilinmiyor';

export interface AgentDailyStats {
  callsAnswered: number;
  callsMissed: number;
  callsOutbound: number;
  transfers: number;
  breakCount: number;
  totalTalkSec: number;
  avgCallSec: number;
  maxCallSec: number;
  firstActivityAt: string | null;
}

export interface LeaderAgentRow {
  id: number;
  extension: string;
  displayName: string;
  online: boolean;
  queueLoggedIn: boolean;
  profile: string | null;
  profileMessage: string | null;
  status: AgentLiveStatus;
  daily: AgentDailyStats;
}

/**
 * Bir agent'ın anlık canlı çağrısı (supervisor monitoring — metadata-only, SES YOK).
 * Backend 'calls:live' topic'inden WS push'lar; AppShell ['live-calls'] cache'ine yazar.
 * userId ile LeaderAgentRow.id üzerinden eşleştirilip isim/ext gösterilir.
 */
export interface LiveCall {
  userId: number;
  username: string;
  role: 'admin' | 'leader' | 'agent';
  state: 'ringing' | 'calling' | 'in_call';
  direction: 'inbound' | 'outbound' | null;
  peer: string | null;
  peerName: string | null;
  callId: string | null;
  /** Agent saatinde (referans). Süre için serverStartedAt/serverAnsweredAt kullan. */
  startedAt: number | null;
  answeredAt: number | null;
  /** SUNUCU saatinde damgalı anchor'lar — süre serverNow() ile bunlardan sayılır
   *  (agent/supervisor saat kayması süreyi bozmaz). */
  serverStartedAt: number;
  serverAnsweredAt: number | null;
  onHold: boolean;
  muted: boolean;
  updatedAt: number;
}

export const Leader = {
  /** Canlı agent tablosu — 3CX durum + günlük metrik. */
  listAgents: () => api.get<LeaderAgentRow[]>('/leader/agents').then((r) => r.data),

  /** Bir agent'ın bugünkü ham aktivitesi (drill-down). */
  agentHistory: (extension: string, limit = 100) =>
    api.get<ActivityLogEntry[]>(`/leader/agents/${extension}/history`, { params: { limit } }).then((r) => r.data),

  /** Uzaktan profil/mola ata. */
  setAgentProfile: (extension: string, profile: AgentProfile, customMessage?: string) =>
    api.post<{ ok: true; extension: string; profile: AgentProfile }>(
      `/leader/agents/${extension}/profile`,
      { profile, ...(customMessage ? { customMessage } : {}) },
    ).then((r) => r.data),

  /** Uzaktan kuyruk login/logout. */
  setAgentQueue: (extension: string, loggedIn: boolean) =>
    api.post<{ ok: true; extension: string; loggedIn: boolean }>(
      `/leader/agents/${extension}/queue`,
      { loggedIn },
    ).then((r) => r.data),
};

// ─── Bildirimler ────────────────────────────────────────────────────────────

export type NotificationType = 'broadcast' | 'fraud' | 'system';

export interface NotificationItem {
  id: number;
  recipientUserId: number | null;
  senderUserId: number | null;
  type: NotificationType;
  title: string | null;
  body: string;
  createdAt: string;
}

export const Notifications = {
  /** Bana + broadcast bildirimler (son N). */
  listMine: (limit = 50) =>
    api.get<NotificationItem[]>('/notifications/me', { params: { limit } }).then((r) => r.data),

  /** Toplu duyuru gönder (leader/admin). */
  broadcast: (body: string, title?: string) =>
    api.post<{ ok: true; notification: NotificationItem }>('/notifications/broadcast', {
      body,
      ...(title ? { title } : {}),
    }).then((r) => r.data),
};

// ─── Performans ─────────────────────────────────────────────────────────────

export type PerfRange = 'today' | 'week' | 'month' | 'custom';

export interface PerfDirectionStats {
  count: number;
  talkSec: number;
  avgSec: number;
  maxSec: number;
  holdSec: number;
  muteSec: number;
}
export interface PerfBucket {
  /** Benzersiz bucket anahtarı (saat/gün/hafta) — sıralama + React key. */
  key: string;
  /** Eksen etiketi: "14:00" (saat), "05.06" (gün), "02-08 Haz" (hafta). */
  label: string;
  answered: number;
  missed: number;
  outbound: number;
  talkSec: number;
}
export interface PerformanceData {
  range: PerfRange;
  from: string;
  to: string;
  answered: number;
  missed: number;
  outboundCount: number;
  transfers: number;
  breakCount: number;
  holdSec: number;
  muteSec: number;
  totalTalkSec: number;
  avgTalkSec: number;
  maxTalkSec: number;
  inbound: PerfDirectionStats;
  outbound: PerfDirectionStats;
  breaks: Array<{ reason: string; count: number; totalSec: number }>;
  trend: PerfBucket[];
}

export const Performance = {
  me: (range: PerfRange = 'today') =>
    api.get<PerformanceData>('/performance/me', { params: { range } }).then((r) => r.data),
};

// ─── Kuyruklar ──────────────────────────────────────────────────────────────

export interface QueueInfo {
  id: number;
  number: string;
  name: string;
  pollingStrategy: string | null;
  agentCount: number;
  loggedInCount: number;
  /** Üye extension numaraları — leader agents ile cross-reference (Kuyruk Durumu). */
  members: string[];
  member?: boolean;
}

export const Queues = {
  /** Mevcut kullanıcının üye olduğu kuyruklar. */
  mine: () => api.get<QueueInfo[]>('/queues/me').then((r) => r.data),
  /** Tüm kuyruklar (leader/admin). */
  all: () => api.get<QueueInfo[]>('/queues').then((r) => r.data),
};

/**
 * Sayfa/sekme kapanırken (beforeunload) güvenilir log — fetch keepalive ile,
 * unload'tan sonra bile tamamlanır. sendBeacon Authorization header set edemediği
 * için fetch+keepalive kullanıyoruz (auth header gerekiyor).
 */
export function logActivityBeacon(type: string, meta?: Record<string, unknown>): void {
  try {
    if (!_accessToken) return;
    fetch(`${apiBase()}/activity-logs`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${_accessToken}` },
      body: JSON.stringify({ type, meta }),
    }).catch(() => { /* best-effort */ });
  } catch { /* */ }
}

export const ActivityLogs = {
  log: (type: string, refId?: string, meta?: Record<string, unknown>) =>
    api.post('/activity-logs', { type, ref_id: refId, meta }).then((r) => r.data),

  /** Current user's recent activity (last N, default 50). */
  listMine: (limit = 50) =>
    api.get<ActivityLogEntry[]>('/activity-logs/me', { params: { limit } }).then((r) => r.data),

  listAdmin: (filter?: {
    user_id?: number;
    type?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) => api.get<ActivityLogEntry[]>('/admin/activity-logs', { params: filter }).then((r) => r.data),
};

export const Admin = {
  listUsers: () => api.get<AdminUser[]>('/admin/users').then((r) => r.data),
  createUser: (input: {
    username: string;
    password: string;
    display_name: string;
    extension: string;
    role: 'admin' | 'leader' | 'agent';
    is_active: boolean;
    auto_answer: boolean;
  }) => api.post<AdminUser>('/admin/users', input).then((r) => r.data),
  updateUser: (id: number, input: Partial<{
    username: string;
    password: string;
    display_name: string;
    extension: string;
    role: 'admin' | 'leader' | 'agent';
    is_active: boolean;
    auto_answer: boolean;
  }>) => api.patch<AdminUser>(`/admin/users/${id}`, input).then((r) => r.data),
  removeUser: (id: number) => api.delete(`/admin/users/${id}`),

  listPbxExtensions: () => api.get<PbxExtension[]>('/admin/pbx-extensions').then((r) => r.data),

  // Speed-dials (admin manages global entries — visible to all agents)
  listGlobalSpeedDials: () => api.get<SpeedDial[]>('/admin/speed-dials').then((r) => r.data),
  createGlobalSpeedDial: (input: { label: string; number: string; position?: number }) =>
    api.post<SpeedDial>('/admin/speed-dials', input).then((r) => r.data),
  updateGlobalSpeedDial: (id: number, input: Partial<{ label: string; number: string; position: number }>) =>
    api.patch<SpeedDial>(`/admin/speed-dials/${id}`, input).then((r) => r.data),
  removeGlobalSpeedDial: (id: number) => api.delete(`/admin/speed-dials/${id}`),

  // CRM Templates (admin manages global templates applied to all agents)
  listGlobalCrmTemplates: () => api.get<CrmTemplate[]>('/admin/crm-templates').then((r) => r.data),
  createGlobalCrmTemplate: (input: CrmTemplateInput) =>
    api.post<CrmTemplate>('/admin/crm-templates', input).then((r) => r.data),
  updateGlobalCrmTemplate: (id: number, input: Partial<CrmTemplateInput>) =>
    api.patch<CrmTemplate>(`/admin/crm-templates/${id}`, input).then((r) => r.data),
  removeGlobalCrmTemplate: (id: number) => api.delete(`/admin/crm-templates/${id}`),
};
